import { useState, useEffect, useRef } from "react";
import { Button, Spin } from "antd";
import { TOKEN_KEY } from "@/authProvider";
import { apiUrlV2, cdnUrl } from "@/consts";

export type MediaItem = { id: string; type: string; name: string; pending?: boolean };
export interface MediaUploadSectionProps {
  initialMedia: MediaItem[];
  onChange?: (media: MediaItem[]) => void;
}

export function MediaUploadSection({ initialMedia, onChange }: MediaUploadSectionProps) {
  console.log("MediaUploadSection render, initialMedia:", initialMedia);
  const [mediaList, setMediaList] = useState<MediaItem[]>(initialMedia.filter(m => !m.pending));
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (onChange) onChange(mediaList);
  }, [mediaList, onChange]);

  const getCdnUrl = (id: string) => `${cdnUrl}/${id}`;

  const handleUploadMultiple = async (files: FileList) => {
    setUploading(true);
    setError(null);
    const filesArr = Array.from(files);
    const now = Date.now();
    // Add all pending with unique ids
    setMediaList((prev: MediaItem[]) => {
      const updated = [
        ...prev,
        ...filesArr.map((file, idx) => ({ id: `pending-${file.name}-${now + idx}`, type: file.type, name: file.name, pending: true }))
      ];
      return updated;
    });
    for (const [idx, file] of filesArr.entries()) {
      setUploadingFileName(file.name);
      const pendingId = `pending-${file.name}-${now + idx}`;
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const body = {
          fileCount: 1,
          mimeType: file.type.startsWith("image") ? "IMAGE" : "VIDEO",
          uploadType: "MISSION",
        };
        const res = await fetch(`${apiUrlV2}/file/presigned_url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const result = await res.json();
        const fileData = result?.data?.[0];
        if (!fileData) throw new Error("No file data returned");
        const formData = new FormData();
        Object.entries(fileData.fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append("Content-Type", file.type);
        formData.append("file", file);
        await fetch(fileData.url, {
          method: "POST",
          body: formData,
        });
        setMediaList((prev: MediaItem[]) => {
          const updated = [
            ...prev.filter((m) => m.id !== pendingId),
            { id: fileData.fileID, type: file.type, name: file.name },
          ];
          return updated;
        });
      } catch (err) {
        setError(`Upload failed for ${file.name}`);
        setMediaList((prev: MediaItem[]) =>
          prev.filter((m) => m.id !== pendingId)
        );
      }
    }
    setUploading(false);
    setUploadingFileName(null);
  };

  const handleRemove = (id: string) => {
    setMediaList((prev: MediaItem[]) =>
      prev.filter((m: MediaItem) => m.id !== id)
    );
  };

  return (
    <div style={{ position: "relative" }}>
      <Button
        type="primary"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        Upload Media
      </Button>
      <input
        ref={fileInputRef}
        id="media-upload-input"
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) handleUploadMultiple(files);
          // Force reset the input so selecting the same file again triggers onChange
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
      />
      {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
      <div
        style={{
          marginTop: 24,
          display: "flex",
          gap: 24,
          flexWrap: "wrap",
          position: "relative",
          minHeight: 150,
        }}
      >
        {mediaList.map((media) => (
          <div
            key={media.id + media.name}
            style={{
              border: "1px solid #4f4e4eff",
              borderRadius: 6,
              padding: 8,
              width: 200,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: 8,
              }}
            >
              {!media.pending && (
                <Button
                  danger
                  size="small"
                  onClick={() => handleRemove(media.id)}
                >
                  Remove
                </Button>
              )}
            </div>
            {media.pending ? (
              <div
                style={{
                  width: "100%",
                  height: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Spin size="large" />
              </div>
            ) : media.type.startsWith("image") || media.type === "IMAGE" ? (
              <img
                src={getCdnUrl(media.id)}
                alt={media.name}
                style={{ width: "100%", maxHeight: 120, objectFit: "cover" }}
              />
            ) : (
              <video
                src={getCdnUrl(media.id)}
                controls
                style={{ width: "100%", maxHeight: 120 }}
              />
            )}
            <div style={{ marginTop: 8, fontSize: 12 }}>{media.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
